import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab
} from '@mui/material';

import {
  Upload,
  CloudUpload,
  CheckCircle,
  Schedule,
  Assignment,
  Download,
  Refresh,
  Dashboard,
  People,
  TrendingUp,
  AccessTime,
  TaskAlt,
  WarningAmber
} from '@mui/icons-material';
import { useClientConfig } from '../context/ClientConfigContext';
import ValidadoLogo from '../components/ValidadoLogo';
import usePageTitle from '../hooks/usePageTitle';
import DidiPortalPage from './DidiPortalPage';

interface UploadRecord {
  id: string;
  nationalId: string;
  status: string;
  uploadDate: string;
  lastUpdated: string;
  completionDate?: string;
  overallResult?: string;
  uploadedBy?: string;
  recordType?: 'client' | 'background_check';
  additionalData?: any;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface ClientUser {
  id: string;
  email: string;
  role: 'manager' | 'user';
  firstName: string;
  lastName: string;
  isActive: boolean;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`client-portal-tabpanel-${index}`}
      aria-labelledby={`client-portal-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const UnifiedClientPortal: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState<ClientUser | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [isDidiUser, setIsDidiUser] = useState(false);
  
  // File upload states
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  
  // Records and stats
  const [records, setRecords] = useState<UploadRecord[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [uploadDateStart, setUploadDateStart] = useState('');
  const [uploadDateEnd, setUploadDateEnd] = useState('');
  const [updatedDateStart, setUpdatedDateStart] = useState('');
  const [updatedDateEnd, setUpdatedDateEnd] = useState('');
  const [stats, setStats] = useState({
    total: 0,
    success_upload: 0,
    under_review: 0,
    passed_to_manual: 0,
    completed: 0
  });

  // Team management (for managers only)
  const [teamMembers, setTeamMembers] = useState<ClientUser[]>([]);
  const [showAddUserDialog, setShowAddUserDialog] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserFirstName, setNewUserFirstName] = useState('');
  const [newUserLastName, setNewUserLastName] = useState('');

  // DiDi credentials for bypass
  const didiCredentials = {
    'viviansantanna@didiglobal.com': '4a5bTQ4I36f8',
    'mcoyramirez@didiglobal.com': '75dM9MPl1Wcf',
    'viviansantanna@99app.com': '4a5bTQ4I36f8',
    'castillo.andres_v@didiglobal.com': '#Ii1_~#7AUpNwJu3T',
    'gabrielwang@didiglobal.com': 'F2pkKT8ikGeuebKz',
    'admin@didi.global': 'Didi2024!',
    'tapia@over.do': 'Hector2024!'
  };

  const clientConfigContext = useClientConfig();
  usePageTitle(clientConfigContext?.config?.companyName ? `${clientConfigContext.config.companyName} Portal` : 'Client Portal');

  // Auto-refresh records every 30 seconds
  useEffect(() => {
    if (isAuthenticated) {
      const interval = setInterval(fetchRecords, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Check for DiDi users first (bypass API)
    const validPassword = didiCredentials[email as keyof typeof didiCredentials];
    if (validPassword && validPassword === password) {
      setIsDidiUser(true);
      return;
    }

    try {
      const response = await fetch('/api/client-portal/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        setIsAuthenticated(true);
        setCurrentUser(data.user);
        localStorage.setItem('clientPortalToken', data.token);
        await fetchRecords();
        if (data.user.role === 'manager') {
          await fetchTeamMembers();
        }
      } else {
        const code = (data && (data.code || data.error || data.name)) || undefined;
        const baseMessage = data?.message || 'Login failed';
        const detailed = response.status >= 500 ? `${baseMessage} (Server error${code ? `: ${code}` : ''}). Try Diagnostics below.` : baseMessage;
        setError(detailed);
      }
    } catch (error) {
      setError('Network error. Please try again.');
    }
  };

  const fetchRecords = async () => {
    try {
      const token = localStorage.getItem('clientPortalToken');
      const response = await fetch('/api/client-portal/records', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setRecords(data.records);
        setStats(data.stats);
      }
    } catch (error) {
      console.error('Error fetching records:', error);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const token = localStorage.getItem('clientPortalToken');
      const response = await fetch('/api/client-portal/team', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setTeamMembers(data.teamMembers);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const handleFileUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!csvFile) return;

    setUploading(true);
    setError('');

    const formData = new FormData();
    formData.append('csvFile', csvFile);

    try {
      const token = localStorage.getItem('clientPortalToken');
      const response = await fetch('/api/client-portal/upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (response.ok) {
        setUploadSuccess(true);
        setCsvFile(null);
        await fetchRecords();
        setTimeout(() => setUploadSuccess(false), 5000);
      } else {
        setError(data.message || 'Upload failed');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleAddUser = async () => {
    try {
      const token = localStorage.getItem('clientPortalToken');
      const response = await fetch('/api/client-portal/team/add', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: newUserEmail,
          firstName: newUserFirstName,
          lastName: newUserLastName,
          role: 'user'
        }),
      });

      if (response.ok) {
        setShowAddUserDialog(false);
        setNewUserEmail('');
        setNewUserFirstName('');
        setNewUserLastName('');
        await fetchTeamMembers();
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to add user');
      }
    } catch (error) {
      setError('Network error. Please try again.');
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCurrentUser(null);
    setEmail('');
    setPassword('');
    setRecords([]);
    setStats({ total: 0, success_upload: 0, under_review: 0, passed_to_manual: 0, completed: 0 });
    setTeamMembers([]);
    localStorage.removeItem('clientPortalToken');
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed': return 'success';
      case 'under_review': return 'warning';
      case 'passed_to_manual': return 'info';
      case 'success_upload': return 'primary';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed': return <CheckCircle />;
      case 'under_review': return <Schedule />;
      case 'passed_to_manual': return <Assignment />;
      default: return <CloudUpload />;
    }
  };

  const escapeCsvField = (value: string | number | null | undefined): string =>
    `"${String(value ?? '').replace(/"/g, '""')}"`;

  const formatNationalIdForExcel = (value: string | undefined): string => {
    const nationalId = String(value ?? '').trim();
    return nationalId ? `="${nationalId}"` : '';
  };

  const normalizeResult = (result?: string) => {
    if (!result) return 'pending';
    if (result === 'records_found') return 'not_clear';
    return result;
  };

  const formatDuration = (hours: number) => {
    if (!Number.isFinite(hours) || hours <= 0) return 'N/A';
    if (hours < 24) return `${Math.round(hours)}h`;
    const days = hours / 24;
    if (days < 10) return `${days.toFixed(1)}d`;
    return `${Math.round(days)}d`;
  };

  const filteredRecords = records.filter((record) => {
    const searchClean = searchTerm.replace(/-/g, '');
    const idClean = (record.nationalId || '').replace(/-/g, '');
    const matchesSearch = !searchTerm || idClean.includes(searchClean);
    const matchesStatus = statusFilter === 'all' || record.status === statusFilter;

    let matchesUploadDate = true;
    if (uploadDateStart) {
      matchesUploadDate = matchesUploadDate && new Date(record.uploadDate) >= new Date(uploadDateStart);
    }
    if (uploadDateEnd) {
      const end = new Date(uploadDateEnd);
      end.setHours(23, 59, 59, 999);
      matchesUploadDate = matchesUploadDate && new Date(record.uploadDate) <= end;
    }

    let matchesUpdatedDate = true;
    if (updatedDateStart) {
      matchesUpdatedDate = matchesUpdatedDate && new Date(record.lastUpdated) >= new Date(updatedDateStart);
    }
    if (updatedDateEnd) {
      const end = new Date(updatedDateEnd);
      end.setHours(23, 59, 59, 999);
      matchesUpdatedDate = matchesUpdatedDate && new Date(record.lastUpdated) <= end;
    }

    return matchesSearch && matchesStatus && matchesUploadDate && matchesUpdatedDate;
  });

  const dashboardMetrics = useMemo(() => {
    const queueStatuses = ['pending', 'active', 'in_progress', 'under_review', 'passed_to_manual'];
    const dashboardRecords = records;
    const completedChecks = dashboardRecords.filter((record) => record.recordType === 'background_check' && record.status === 'completed');
    const inQueue = dashboardRecords.filter((record) => queueStatuses.includes(record.status)).length;
    const clearCount = completedChecks.filter((record) => normalizeResult(record.overallResult) === 'clear').length;
    const notClearCount = completedChecks.filter((record) => normalizeResult(record.overallResult) === 'not_clear').length;

    const processingHours = completedChecks
      .map((record) => {
        const start = new Date(record.uploadDate).getTime();
        const endSource = record.completionDate || record.lastUpdated;
        const end = new Date(endSource).getTime();
        if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) {
          return null;
        }
        return (end - start) / (1000 * 60 * 60);
      })
      .filter((value): value is number => value !== null)
      .sort((a, b) => a - b);

    const averageHours = processingHours.length
      ? processingHours.reduce((sum, value) => sum + value, 0) / processingHours.length
      : 0;

    const medianHours = processingHours.length
      ? processingHours.length % 2 === 0
        ? (processingHours[processingHours.length / 2 - 1] + processingHours[processingHours.length / 2]) / 2
        : processingHours[Math.floor(processingHours.length / 2)]
      : 0;

    const fastestHours = processingHours.length ? processingHours[0] : 0;
    const slowestHours = processingHours.length ? processingHours[processingHours.length - 1] : 0;
    const total = dashboardRecords.length;
    const completionRate = total ? (completedChecks.length / total) * 100 : 0;
    const decidedCount = clearCount + notClearCount;
    const clearRate = decidedCount ? (clearCount / decidedCount) * 100 : 0;
    const resolvedLast7Days = completedChecks.filter((record) => {
      const endSource = record.completionDate || record.lastUpdated;
      const end = new Date(endSource).getTime();
      return Number.isFinite(end) && end >= Date.now() - (7 * 24 * 60 * 60 * 1000);
    }).length;

    return {
      total,
      inQueue,
      filteredCompleted: completedChecks.length,
      clearCount,
      notClearCount,
      averageHours,
      medianHours,
      fastestHours,
      slowestHours,
      completionRate,
      clearRate,
      resolvedLast7Days
    };
  }, [records]);

  if (isDidiUser) {
    return <DidiPortalPage autoLoginEmail={email} />;
  }

  const handleDownloadFilteredChecks = () => {
    if (filteredRecords.length === 0) {
      setError('No hay checks filtrados para descargar.');
      return;
    }

    const headers = ['National ID', 'Status', 'Result', 'Uploaded By', 'Upload Date', 'Last Updated'];
    const csvContent = '\uFEFF' + [
      headers.join(','),
      ...filteredRecords.map((record) => [
        escapeCsvField(formatNationalIdForExcel(record.nationalId)),
        escapeCsvField(record.status),
        escapeCsvField(record.overallResult || ''),
        escapeCsvField(record.uploadedBy || ''),
        escapeCsvField(new Date(record.uploadDate).toLocaleDateString()),
        escapeCsvField(new Date(record.lastUpdated).toLocaleDateString())
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `gabriel_filtered_checks_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (!isAuthenticated) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <ValidadoLogo />
            <Typography variant="h4" component="h1" gutterBottom sx={{ mt: 2 }}>
              Client Portal
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Upload documents and track your background check progress
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
              <Box sx={{ mt: 1 }}>
                <Button size="small" variant="outlined" href="/api/diagnostics" target="_blank">Open Diagnostics</Button>
              </Box>
            </Alert>
          )}

          <form onSubmit={handleLogin}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={email}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              margin="normal"
              required
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              size="large"
            >
              Sign In
            </Button>
          </form>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Welcome, {currentUser?.firstName} {currentUser?.lastName}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {currentUser?.role === 'manager' ? 'Company Manager' : 'Team Member'} • {currentUser?.email}
          </Typography>
        </Box>
        <Button variant="outlined" onClick={handleLogout}>
          Sign Out
        </Button>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(_: React.SyntheticEvent, newValue: number) => setTabValue(newValue)}>
          <Tab icon={<Dashboard />} label="Dashboard" />
          <Tab icon={<Assignment />} label="Records" />
          <Tab icon={<Upload />} label="Upload Files" />
          {currentUser?.role === 'manager' && (
            <Tab icon={<People />} label="Team Management" />
          )}
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {/* Dashboard Tab */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" gutterBottom>
            Background Check Intelligence
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Executive view of your uploaded checks, outcomes, and processing speed.
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 2, mb: 4 }}>
            <Card sx={{ borderRadius: 3, background: 'linear-gradient(135deg, #0f172a 0%, #1d4ed8 100%)', color: 'white' }}>
              <CardContent>
                <Typography sx={{ opacity: 0.8 }} gutterBottom>Total Submissions</Typography>
                <Typography variant="h4">{dashboardMetrics.total}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.75 }}>
                  Visible in current view
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>Pending or In Progress</Typography>
                    <Typography variant="h4" color="primary">{dashboardMetrics.inQueue}</Typography>
                  </Box>
                  <Schedule color="primary" />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Active operational queue
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>Completed</Typography>
                    <Typography variant="h4" color="success.main">{dashboardMetrics.filteredCompleted}</Typography>
                  </Box>
                  <TaskAlt color="success" />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {dashboardMetrics.completionRate.toFixed(1)}% completion rate
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>Clear</Typography>
                    <Typography variant="h4" color="success.main">{dashboardMetrics.clearCount}</Typography>
                  </Box>
                  <CheckCircle color="success" />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Positive final decisions
                </Typography>
              </CardContent>
            </Card>
            <Card sx={{ borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography color="text.secondary" gutterBottom>Not Clear</Typography>
                    <Typography variant="h4" color="error.main">{dashboardMetrics.notClearCount}</Typography>
                  </Box>
                  <WarningAmber color="error" />
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Flagged final decisions
                </Typography>
              </CardContent>
            </Card>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 1.4fr) minmax(320px, 1fr)', gap: 2, mb: 4 }}>
            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" gutterBottom>
                Result Mix
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Final outcomes for completed checks in the current view.
              </Typography>

              <Box sx={{ mb: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography variant="body2">Clear</Typography>
                  <Typography variant="body2" fontWeight={600}>{dashboardMetrics.clearCount}</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={dashboardMetrics.filteredCompleted ? (dashboardMetrics.clearCount / dashboardMetrics.filteredCompleted) * 100 : 0}
                  sx={{ height: 10, borderRadius: 10, backgroundColor: 'rgba(25, 118, 210, 0.08)', '& .MuiLinearProgress-bar': { backgroundColor: '#2e7d32' } }}
                />
              </Box>

              <Box sx={{ mb: 2.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography variant="body2">Not Clear</Typography>
                  <Typography variant="body2" fontWeight={600}>{dashboardMetrics.notClearCount}</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={dashboardMetrics.filteredCompleted ? (dashboardMetrics.notClearCount / dashboardMetrics.filteredCompleted) * 100 : 0}
                  sx={{ height: 10, borderRadius: 10, backgroundColor: 'rgba(25, 118, 210, 0.08)', '& .MuiLinearProgress-bar': { backgroundColor: '#d32f2f' } }}
                />
              </Box>

              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75 }}>
                  <Typography variant="body2">Decided Checks</Typography>
                  <Typography variant="body2" fontWeight={600}>{dashboardMetrics.clearCount + dashboardMetrics.notClearCount}</Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={dashboardMetrics.filteredCompleted ? ((dashboardMetrics.clearCount + dashboardMetrics.notClearCount) / dashboardMetrics.filteredCompleted) * 100 : 0}
                  sx={{ height: 10, borderRadius: 10, backgroundColor: 'rgba(25, 118, 210, 0.08)', '& .MuiLinearProgress-bar': { backgroundColor: '#1d4ed8' } }}
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Clear Share</Typography>
                  <Typography variant="h5">{dashboardMetrics.clearRate.toFixed(1)}%</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Resolved Last 7 Days</Typography>
                  <Typography variant="h5">{dashboardMetrics.resolvedLast7Days}</Typography>
                </Box>
              </Box>
            </Paper>

            <Paper sx={{ p: 3, borderRadius: 3 }}>
              <Typography variant="h6" gutterBottom>
                Processing Insights
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Based on completed checks using upload date versus completion date.
              </Typography>

              <Box sx={{ display: 'grid', gap: 2 }}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography color="text.secondary" gutterBottom>Average Processing Time</Typography>
                      <Typography variant="h5">{formatDuration(dashboardMetrics.averageHours)}</Typography>
                    </Box>
                    <AccessTime color="primary" />
                  </CardContent>
                </Card>

                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box>
                      <Typography color="text.secondary" gutterBottom>Median Processing Time</Typography>
                      <Typography variant="h5">{formatDuration(dashboardMetrics.medianHours)}</Typography>
                    </Box>
                    <TrendingUp color="action" />
                  </CardContent>
                </Card>

                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>Fastest</Typography>
                      <Typography variant="h6">{formatDuration(dashboardMetrics.fastestHours)}</Typography>
                    </CardContent>
                  </Card>
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>Slowest</Typography>
                      <Typography variant="h6">{formatDuration(dashboardMetrics.slowestHours)}</Typography>
                    </CardContent>
                  </Card>
                </Box>
              </Box>
            </Paper>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 2, mb: 4 }}>
            <Paper sx={{ p: 2.5, borderRadius: 3 }}>
              <Typography variant="body2" color="text.secondary">API Total</Typography>
              <Typography variant="h5">{stats.total}</Typography>
            </Paper>
            <Paper sx={{ p: 2.5, borderRadius: 3 }}>
              <Typography variant="body2" color="text.secondary">Under Review</Typography>
              <Typography variant="h5">{stats.under_review}</Typography>
            </Paper>
            <Paper sx={{ p: 2.5, borderRadius: 3 }}>
              <Typography variant="body2" color="text.secondary">Manual Review</Typography>
              <Typography variant="h5">{stats.passed_to_manual}</Typography>
            </Paper>
            <Paper sx={{ p: 2.5, borderRadius: 3 }}>
              <Typography variant="body2" color="text.secondary">Current Queue</Typography>
              <Typography variant="h5">{dashboardMetrics.inQueue}</Typography>
            </Paper>
          </Box>
        </Box>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="in_progress">In Progress</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="under_review">Under Review</MenuItem>
                <MenuItem value="passed_to_manual">Manual Review</MenuItem>
                <MenuItem value="active">Active</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              label="Upload Date Start"
              type="date"
              value={uploadDateStart}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUploadDateStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              label="Upload Date End"
              type="date"
              value={uploadDateEnd}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUploadDateEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              label="Updated Start"
              type="date"
              value={updatedDateStart}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUpdatedDateStart(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              label="Updated End"
              type="date"
              value={updatedDateEnd}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUpdatedDateEnd(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            {(statusFilter !== 'all' || uploadDateStart || uploadDateEnd || updatedDateStart || updatedDateEnd) && (
              <Button
                size="small"
                onClick={() => {
                  setStatusFilter('all');
                  setUploadDateStart('');
                  setUploadDateEnd('');
                  setUpdatedDateStart('');
                  setUpdatedDateEnd('');
                }}
              >
                Clear Filters
              </Button>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              placeholder="Search by ID (with or without dashes)..."
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              sx={{ width: 300 }}
            />
            <Button
              variant="outlined"
              startIcon={<Download />}
              onClick={handleDownloadFilteredChecks}
            >
              Download Filtered
            </Button>
          </Box>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>National ID</TableCell>
                <TableCell>Status / Result</TableCell>
                <TableCell>Uploaded By</TableCell>
                <TableCell>Upload Date</TableCell>
                <TableCell>Last Updated</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRecords.map((record) => (
                <TableRow key={record.id}>
                  <TableCell>{record.nationalId}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                      <Chip
                        icon={getStatusIcon(record.status)}
                        label={record.status.replace('_', ' ').toUpperCase()}
                        color={getStatusColor(record.status) as any}
                        size="small"
                      />
                      {(record as any).overallResult && (record as any).overallResult !== 'pending' && (
                        <Chip
                          label={(record as any).overallResult.replace('_', ' ').toUpperCase()}
                          color={(record as any).overallResult === 'clear' ? 'success' : 'error'}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{(record as any).uploadedBy || 'Unknown'}</TableCell>
                  <TableCell>{new Date(record.uploadDate).toLocaleDateString()}</TableCell>
                  <TableCell>{new Date(record.lastUpdated).toLocaleDateString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {/* Upload Tab */}
        <Paper sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom>
            Upload Background Check Files
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Upload a CSV file containing national IDs and personal information for background checks.
          </Typography>

          {uploadSuccess && (
            <Alert severity="success" sx={{ mb: 3 }}>
              File uploaded successfully! Processing will begin shortly.
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleFileUpload}>
            <Box sx={{ mb: 3 }}>
              <input
                accept=".csv"
                style={{ display: 'none' }}
                id="csv-file-input"
                type="file"
                onChange={(e) => setCsvFile(e.target.files?.[0] || null)}
              />
              <label htmlFor="csv-file-input">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<CloudUpload />}
                  sx={{ mr: 2 }}
                >
                  Choose CSV File
                </Button>
              </label>
              {csvFile && (
                <Typography variant="body2" component="span">
                  Selected: {csvFile.name}
                </Typography>
              )}
            </Box>

            <Button
              type="submit"
              variant="contained"
              disabled={!csvFile || uploading}
              startIcon={<Upload />}
              size="large"
            >
              {uploading ? 'Uploading...' : 'Upload File'}
            </Button>

            {uploading && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress />
              </Box>
            )}
          </form>
        </Paper>
      </TabPanel>

      {currentUser?.role === 'manager' && (
        <TabPanel value={tabValue} index={3}>
          {/* Team Management Tab */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5">
              Team Management
            </Typography>
            <Button
              variant="contained"
              startIcon={<People />}
              onClick={() => setShowAddUserDialog(true)}
            >
              Add Team Member
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {teamMembers.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>{member.firstName} {member.lastName}</TableCell>
                    <TableCell>{member.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={member.role.toUpperCase()}
                        color={member.role === 'manager' ? 'primary' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={member.isActive ? 'ACTIVE' : 'INACTIVE'}
                        color={member.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      )}

      {/* Add User Dialog */}
      <Dialog open={showAddUserDialog} onClose={() => setShowAddUserDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Team Member</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={newUserEmail}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewUserEmail(e.target.value)}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="First Name"
            value={newUserFirstName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewUserFirstName(e.target.value)}
            margin="normal"
            required
          />
          <TextField
            fullWidth
            label="Last Name"
            value={newUserLastName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewUserLastName(e.target.value)}
            margin="normal"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddUserDialog(false)}>Cancel</Button>
          <Button onClick={handleAddUser} variant="contained">Add User</Button>
        </DialogActions>
      </Dialog>

      <Fab
        color="primary"
        aria-label="refresh"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={fetchRecords}
      >
        <Refresh />
      </Fab>
    </Container>
  );
};

export default UnifiedClientPortal;
